import { devsmartApi } from '../../utils/api';
import type { IMensajeChat } from '../../globales/contextos/asistenteIAContext';

export interface ChatSessionDto {
    id: string;
    title: string;
    providerName: string;
    modelName: string;
    messageCount: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    createdAt: string;
    updatedAt: string;
    isActive: boolean;
}

export interface AiMessageDto {
    id: string;
    role: number | string; // MessageRole: 0=System, 1=User, 2=Assistant
    content: string;
    sequenceNumber: number;
    createdAt: string;
}

class ModeloDevsmartAI {
    constructor() { }

    /**
     * Crea una nueva sesión de chat en el backend.
     */
    public async crearSesion(titulo?: string, dataSourceId?: string): Promise<string> {
        try {
            const systemPrompt = `You are a specialized operational assistant for managers and analysts of the platform.
The users interacting with you are analysts and managers who need to monitor, analyze, and query the real-time state of their operations.
Key operational topics they will ask about include:
- Active or historic emergencies, alerts, and critical issues.
- Tasks in progress, including preventive and corrective maintenance.
- Operational performance, index of compliance, SLAs, and technical routines.
- Pending works, technician routing, and resource allocation.
Format your responses to be highly structured, professional, and clear, using markdown tables or bullet points where appropriate, as they are reading reports for decision-making.
Keep all answers strictly focused on the operations and technical data context provided.
Always reply in Spanish by default.`;

            const requestBody = {
                title: titulo || "Nueva conversación",
                dataSourceId: dataSourceId || null,
                providerName: "deepseek",
                modelName: "deepseek-v4-flash",
                systemPrompt: systemPrompt
            };
            const { data } = await devsmartApi.post<any>(`/api/chat/sessions`, requestBody);
            const id = data.id || data.Id;
            if (!id) {
                throw new Error("No se pudo obtener el ID de la sesión creada.");
            }
            return id;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene todos los mensajes de una sesión existente, filtrando los del sistema.
     */
    public async obtenerMensajes(sessionId: string): Promise<IMensajeChat[]> {
        try {
            const { data } = await devsmartApi.get<any[]>(`/api/chat/sessions/${sessionId}/messages`);
            return data
                .filter((m) => {
                    const role = m.role ?? m.Role;
                    const roleLower = String(role).toLowerCase();
                    return roleLower !== 'system' && role !== 0;
                })
                .map((m) => {
                    const role = m.role ?? m.Role;
                    const roleLower = String(role).toLowerCase();
                    const remitente: 'usuario' | 'agente' = (roleLower === 'user' || role === 1) ? 'usuario' : 'agente';
                    
                    const content = m.content ?? m.Content ?? '';
                    const createdAt = m.createdAt ?? m.CreatedAt;
                    
                    return {
                        id: m.id || m.Id || `msg-${Date.now()}-${Math.random()}`,
                        remitente,
                        texto: content,
                        fecha: createdAt ? new Date(createdAt) : new Date()
                    };
                });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Envía un mensaje y obtiene la respuesta de la IA en formato completo.
     */
    public async enviarMensaje(sessionId: string, contenido: string): Promise<string> {
        try {
            const requestBody = {
                content: contenido
            };
            const { data } = await devsmartApi.post<any>(`/api/chat/sessions/${sessionId}/messages`, requestBody);
            const content = data.content ?? data.Content;
            if (content === undefined || content === null) {
                throw new Error("La respuesta del agente no contiene texto.");
            }
            return content;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cierra la sesión de chat de manera lógica en el backend (soft-delete).
     */
    public async cerrarSesion(sessionId: string): Promise<void> {
        try {
            await devsmartApi.delete(`/api/chat/sessions/${sessionId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene el DataSource filtrando por su nombre.
     */
    public async obtenerDataSourcePorNombre(nombre: string): Promise<any> {
        try {
            const { data } = await devsmartApi.get<any>(`/api/datasources/by-name/${nombre}`);
            return data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene los detalles de una sesión de chat existente (incluyendo el dataSourceId).
     */
    public async obtenerDetallesSesion(sessionId: string): Promise<any> {
        try {
            const { data } = await devsmartApi.get<any>(`/api/chat/sessions/${sessionId}`);
            return data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Consulta datos de un DataSource específico utilizando lenguaje natural (NL2SQL).
     */
    public async consultarDatos(dataSourceId: string, pregunta: string): Promise<any> {
        try {
            const requestBody = {
                datasourceId: dataSourceId,
                question: pregunta
            };
            const { data } = await devsmartApi.post<any>(`/api/datasources/query`, requestBody);
            return data;
        } catch (error) {
            throw error;
        }
    }
}

export default ModeloDevsmartAI;
export { ModeloDevsmartAI };
